import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const KindleBonus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const bookParam = searchParams.get('book'); // 're-god' or 'jiritsu'

  const [status, setStatus] = useState('loading'); // 'loading' | 'no_actor' | 'need_kyc' | 'ready' | 'claimed'
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  const getPageTitle = () => {
    return '書籍読了特典受取';
  };

  const getBookTitle = () => {
    if (data?.book_title) return data.book_title;
    if (bookParam === 're-god') return 'RE：神との対話';
    if (bookParam === 'jiritsu') return '自律評価経済';
    return '書籍';
  };

  const getBadgeName = () => {
    if (data?.badge_name) return data.badge_name;
    if (bookParam === 're-god') return 'RE：神との対話 読了バッジ';
    if (bookParam === 'jiritsu') return '自律評価経済 読了バッジ';
    return 'バッジ';
  };

  useEffect(() => {
    document.title = `${getPageTitle()} | 真決DAO`;
  }, [status]);

  useEffect(() => {
    if (bookParam) {
      checkEligibility();
    } else {
      setError('書籍パラメータが指定されていません');
      setStatus('error');
    }
  }, [bookParam]);

  const checkEligibility = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 Kindle check - token:', token ? 'exists' : 'none');
      console.log('🔍 Kindle check - bookParam:', bookParam);
      
      if (!token) {
        console.log('❌ No token, setting no_actor');
        setStatus('no_actor');
        return;
      }

      const apiUrl = `${import.meta.env.VITE_API_URL}/api/special-rewards/kindle-check?book=${bookParam}`;
      console.log('📡 Calling API:', apiUrl);
      const res = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ API response:', res.data);
      setStatus(res.data.status);
      setData(res.data);
    } catch (error) {
      console.error('❌ Eligibility check error:', error);
      console.error('Response:', error.response?.data);
      if (error.response?.status === 401) {
        setStatus('no_actor');
      } else {
        setError(error.response?.data?.message || '資格確認中にエラーが発生しました');
        setStatus('error');
      }
    }
  };

  const handleClaim = async () => {
    setClaiming(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${import.meta.env.VITE_API_URL}/api/special-rewards/kindle-claim`,
        { book_code: data.book_code },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        if (res.data.token_reward) {
          window.dispatchEvent(new window.CustomEvent('tokenMined', {
            detail: {
              tokenSymbol: 'SHINKETSU',
              amount: res.data.token_reward.amount,
              source: 'Kindle特典',
              isConsumption: false,
              icon: '🎁'
            }
          }));
          
          setTimeout(() => {
            window.dispatchEvent(new window.CustomEvent('walletBalanceUpdate'));
          }, 1000);
        }
        
        setStatus('claimed');
        setData({ ...data, claimed_at: res.data.claimed_at });
      }
    } catch (error) {
      console.error('Claim error:', error);
      setError(error.response?.data?.message || '特典受取に失敗しました');
    } finally {
      setClaiming(false);
    }
  };

  // 【画面A】新規登録案内
  if (status === 'no_actor') {
    return (
      <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4'>
          <div className='max-w-md w-full bg-white rounded-2xl shadow-xl p-8'>
          <div className='text-center mb-6'>
            <div className='text-6xl mb-4'>🎁</div>
            <h1 className='text-3xl font-bold text-gray-800 mb-2'>Kindle特典へようこそ</h1>
          </div>

          <p className='text-gray-700 mb-4 text-center'>
            『{getBookTitle()}』をご購入いただきありがとうございます！
          </p>

          <p className='text-gray-700 mb-6 text-center'>
            特典を受け取るには、真決DAOの本会員登録が必要です。
          </p>

          <div className='bg-blue-50 p-6 rounded-xl mb-6 border-2 border-blue-200'>
            <p className='font-bold text-gray-800 mb-3 text-lg'>【特典内容】</p>
            <div className='space-y-2 text-gray-700'>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2'>✅</span>
                1,000 SH
              </p>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2'>✅</span>
                {getBadgeName()}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(`/register?kindle=${bookParam}`)}
            className='w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold text-lg shadow-lg'
          >
            新規登録する（無料）
          </button>

          <p className='text-sm text-gray-500 text-center mt-4'>
            ※本会員登録完了後、このページから特典を受け取れます
          </p>
        </div>
      </div>
    );
  }

  // 【画面B】本会員登録案内
  if (status === 'need_kyc') {
    return (
      <div className='min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center p-4'>
        <div className='max-w-md w-full bg-white rounded-2xl shadow-xl p-8'>
          <div className='text-center mb-6'>
            <div className='text-6xl mb-4'>🎁</div>
            <h1 className='text-3xl font-bold text-gray-800 mb-2'>Kindle特典へようこそ</h1>
          </div>

          <p className='text-gray-700 mb-4 text-center'>
            『{data?.book_title}』をご購入いただきありがとうございます！
          </p>

          <p className='text-gray-700 mb-4 text-center'>
            特典を受け取るには、本会員登録（KYC認証）が必要です。
          </p>

          <div className='bg-amber-50 p-4 rounded-xl mb-6 border-2 border-amber-200'>
            <p className='text-gray-700 text-center'>
              現在のステータス: <span className='font-bold text-amber-700'>{data?.kyc_status || 'Lv1'}</span>
            </p>
          </div>

          <div className='bg-blue-50 p-6 rounded-xl mb-6 border-2 border-blue-200'>
            <p className='font-bold text-gray-800 mb-3 text-lg'>【特典内容】</p>
            <div className='space-y-2 text-gray-700'>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2'>✅</span>
                1,000 SH
              </p>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2'>✅</span>
                {data?.badge_name}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate('/kyc')}
            className='w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 rounded-xl hover:from-orange-700 hover:to-red-700 transition-all duration-200 font-bold text-lg shadow-lg'
          >
            本会員登録を進める
          </button>

          <p className='text-sm text-gray-500 text-center mt-4'>
            ※本会員登録完了後、このページから特典を受け取れます
          </p>
        </div>
      </div>
    );
  }

  // 【画面C】特典受取ボタン表示
  if (status === 'ready') {
    return (
      <div className='min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4'>
        <div className='max-w-md w-full bg-white rounded-2xl shadow-xl p-8'>
          <div className='text-center mb-6'>
            <div className='text-6xl mb-4'>🎉</div>
            <h1 className='text-3xl font-bold text-gray-800 mb-2'>特典を受け取れます！</h1>
          </div>

          <p className='text-gray-700 mb-4 text-center'>
            『{data?.book_title}』をご購入いただきありがとうございます！
          </p>

          <p className='text-gray-700 mb-6 text-center'>
            以下の特典を受け取ることができます：
          </p>

          <div className='bg-green-50 p-6 rounded-xl mb-6 border-2 border-green-300'>
            <p className='font-bold text-gray-800 mb-3 text-lg'>【特典内容】</p>
            <div className='space-y-2 text-gray-700'>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2 text-xl'>✅</span>
                <span className='text-lg'>1,000 SH</span>
              </p>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2 text-xl'>✅</span>
                <span className='text-lg'>{data?.badge_name}</span>
              </p>
            </div>
          </div>

          {error && (
            <div className='bg-red-50 border-2 border-red-300 text-red-700 px-4 py-3 rounded-xl mb-4'>
              {error}
            </div>
          )}

          <button
            onClick={handleClaim}
            disabled={claiming}
            className='w-full bg-gradient-to-r from-red-600 to-pink-600 text-white py-4 rounded-xl hover:from-red-700 hover:to-pink-700 transition-all duration-200 font-bold text-lg shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed'
          >
            {claiming ? '受取中...' : '特典を受け取る'}
          </button>

          <p className='text-sm text-gray-500 text-center mt-4'>
            ※特典は1回のみ受け取れます
          </p>
        </div>
      </div>
    );
  }

  // 【画面D】受取済み表示
  if (status === 'claimed') {
    return (
      <div className='min-h-screen bg-gradient-to-br from-gray-50 to-slate-100 flex items-center justify-center p-4'>
        <div className='max-w-md w-full bg-white rounded-2xl shadow-xl p-8'>
          <div className='text-center mb-6'>
            <div className='text-6xl mb-4'>✅</div>
            <h1 className='text-3xl font-bold text-gray-800 mb-2'>特典受取済み</h1>
          </div>

          <p className='text-gray-700 mb-6 text-center'>
            『{getBookTitle()}』の特典はすでに受け取り済みです。
          </p>

          <div className='bg-gray-50 p-6 rounded-xl mb-6 border-2 border-gray-300'>
            <p className='font-bold text-gray-800 mb-3 text-lg'>【受け取った特典】</p>
            <div className='space-y-2 text-gray-700 mb-4'>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2'>✅</span>
                1,000 SH
              </p>
              <p className='flex items-center'>
                <span className='text-green-600 mr-2'>✅</span>
                {getBadgeName()}
              </p>
            </div>
            {data?.claimed_at && (
              <p className='text-sm text-gray-500 mt-3'>
                受取日: {new Date(data.claimed_at).toLocaleDateString('ja-JP')}
              </p>
            )}
          </div>

          <button
            onClick={() => navigate('/')}
            className='w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-bold text-lg shadow-lg'
          >
            真決DAOホームへ
          </button>
        </div>
      </div>
    );
  }

  // エラー表示
  if (status === 'error' || error) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center p-4'>
        <div className='max-w-md w-full bg-white rounded-2xl shadow-xl p-8'>
          <div className='text-center mb-6'>
            <div className='text-6xl mb-4'>⚠️</div>
            <h1 className='text-3xl font-bold text-gray-800 mb-2'>エラー</h1>
          </div>
          <p className='text-gray-700 mb-6 text-center'>{error || '不明なエラーが発生しました'}</p>
          <button
            onClick={() => navigate('/')}
            className='w-full bg-gradient-to-r from-gray-600 to-slate-600 text-white py-4 rounded-xl hover:from-gray-700 hover:to-slate-700 transition-all duration-200 font-bold text-lg shadow-lg'
          >
            ホームへ戻る
          </button>
        </div>
      </div>
    );
  }

  // ローディング
  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4'>
      <div className='max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center'>
        <div className='animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4'></div>
        <p className='text-gray-700 text-lg'>読込中...</p>
      </div>
    </div>
  );
};

export default KindleBonus;